import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { getStates, getCities } from '../../../api/states';

/**
 * ModalEditarSede - Modal form to edit sede information
 * CRITICAL: Always includes cliente field in submit payload to prevent validation errors
 * @param {Object} props - Component props
 * @param {boolean} props.show - Modal visibility state
 * @param {Function} props.onHide - Handler to close modal
 * @param {Object} props.sede - Sede object to edit
 * @param {string} props.clienteId - Cliente ID to include in payload
 * @param {Function} props.onSubmit - Handler receiving updated sede data
 * @param {boolean} props.loading - Loading state for submit button
 * @returns {JSX.Element}
 */
const ModalEditarSede = ({ show, onHide, sede, clienteId, onSubmit, loading = false, mode = 'edit', initialData = null }) => {
    const [form, setForm] = useState({
        nameSede: '',
        departamento: '',
        ciudad: '',
        dirSede: '',
        contSede: '',
        celContacto: '',
        emailContacto: ''
    });

    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [errors, setErrors] = useState({});

    // Load states on mount
    useEffect(() => {
        if (show) {
            loadStates();
        }
    }, [show]);

    // Reset form when opening in create mode
    useEffect(() => {
        if (show && mode === 'create') {
            setForm({
                nameSede: '',
                departamento: initialData?.departamento || '',
                ciudad: initialData?.ciudad || '',
                dirSede: '',
                contSede: initialData?.contSede || '',
                celContacto: initialData?.celContacto || '',
                emailContacto: initialData?.emailContacto || ''
            });
            setErrors({});
        }
    }, [show, mode]);

    // Populate form from sede prop
    useEffect(() => {
        if (sede) {
            setForm({
                nameSede: sede.nameSede || '',
                departamento: sede.departamento || '',
                ciudad: sede.ciudad || '',
                dirSede: sede.dirSede || '',
                contSede: sede.contSede || '',
                celContacto: sede.celContacto || '',
                emailContacto: sede.emailContacto || ''
            });
        }
    }, [sede]);

    // Load cities when departamento changes
    useEffect(() => {
        if (form.departamento && show) {
            loadCities(form.departamento);
        } else {
            setCities([]);
        }
    }, [form.departamento, show]);

    const loadStates = async () => {
        setLoadingStates(true);
        try {
            const stateDB = await getStates();
            const st = await stateDB.json();
            setStates(st);
        } catch (error) {
            console.error('Error loading states:', error);
        } finally {
            setLoadingStates(false);
        }
    };

    const loadCities = async (departamento) => {
        setLoadingCities(true);
        try {
            const cityDb = await getCities(departamento);
            const ct = await cityDb.json();
            setCities(ct);
        } catch (error) {
            console.error('Error loading cities:', error);
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!form.nameSede.trim()) {
            newErrors.nameSede = 'El nombre de la sede es requerido';
        }
        
        if (!form.ciudad.trim()) {
            newErrors.ciudad = 'La ciudad es requerida';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            if (mode === 'create') {
                onSubmit({ cliente: clienteId, ...form });
            } else {
                // CRITICAL: Always include cliente in payload
                onSubmit({ _id: sede._id, cliente: clienteId, ...form });
            }
        }
    };

    const handleClose = () => {
        setErrors({});
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{mode === 'create' ? 'Crear Nueva Sede' : 'Editar Sede'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre de Sede *</Form.Label>
                            <Form.Control
                                type="text"
                                name="nameSede"
                                value={form.nameSede}
                                onChange={handleChange}
                                isInvalid={!!errors.nameSede}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.nameSede}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Departamento</Form.Label>
                            <Form.Select
                                name="departamento"
                                value={form.departamento}
                                onChange={handleChange}
                                disabled={loadingStates}
                            >
                                <option value="">Seleccione...</option>
                                {states.map((state, idx) => (
                                    <option key={idx} value={state.name}>
                                        {state.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ciudad *</Form.Label>
                            <Form.Select
                                name="ciudad"
                                value={form.ciudad}
                                onChange={handleChange}
                                disabled={loadingCities || cities.length === 0}
                                isInvalid={!!errors.ciudad}
                            >
                                <option value="">Seleccione...</option>
                                {cities.map((city, idx) => (
                                    <option key={idx} value={city.name}>
                                        {city.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {errors.ciudad}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Dirección</Form.Label>
                            <Form.Control
                                type="text"
                                name="dirSede"
                                value={form.dirSede}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Label>Contacto</Form.Label>
                    <Form.Control
                        type="text"
                        name="contSede"
                        value={form.contSede}
                        onChange={handleChange}
                    />
                </Form.Group>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Celular de Contacto</Form.Label>
                            <Form.Control
                                type="text"
                                name="celContacto"
                                value={form.celContacto}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email de Contacto</Form.Label>
                            <Form.Control
                                type="email"
                                name="emailContacto"
                                value={form.emailContacto}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Guardando...' : mode === 'create' ? 'Crear Sede' : 'Guardar Cambios'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalEditarSede;
